import { useState } from "react";
import { usePanes, useRefetchWhenPaneSaves } from "../../../main/panes/usePanes";
import RiskDocumentationPane from "../riskDocumentationPane/RiskDocumentationPane";
import { useNavigate } from "react-router";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { FilterOption, FilterOptionType, RiskFieldGrouping } from "@common/components/filterPopover/FilterPopover.types";
import { DateFieldValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu";
import { GetDateData, GetListData, GetNumberData } from "@common/components/filterPopover/subMenues/SubMenuDataHelper";
import { NumberIntervalValue } from "@common/components/filterPopover/subMenues/NumberFieldSubMenu";
import { FilterOperatorUi } from "@common/api/filterTypes";
import { FilterSortState } from "@common/hooks/useFilterState";
import { applyClientFilters, hasDateRange, matchesDateFilter, matchesNumberFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { TableFields } from "@common/components/tableDisplayFields/TableFields.types";
import { usePageView } from "@common/pageView/PageView.hooks";
import { FilterPage, SessionStorageSavedView } from "@common/pageView/PageView.types";
import { useSavedViewQuery } from "@common/components/savedViews/SavedViewsMenu.hooks";
import { SavedView } from "@common/components/savedViews/SavedViewsMenu.types";
import { IdNamePair } from "../../../customLists/customListView/CustomListView.types";
import { useUrlProvider } from "../../../useUrlProvider";
import { mitigationStatusTranslationKey } from "../../riskMitigationStatus";
import { RiskTreatmentType, treatmentChipTranslationKey } from "../../riskTreatmentType";
import { allRiskAssessmentStatuses, nonRetiredStatuses, riskAssessmentStatusTranslationKey } from "../../RiskAssessmentStatus";
import { assessmentConfidenceTranslationKey, getAssessmentConfidenceOptions } from "../../AssessmentConfidence";
import {
    allRiskAssessmentColumns,
    RiskAssessmentColumn,
    RiskAssessmentExportHeader,
    RiskAssessmentOverviewResult,
    RiskAssessmentsSavedFilter,
    RiskAssessmentSubject,
    RiskScenarioRow,
} from "../RiskAssessmentsOverview.types";
import {
    getDisplayedScenarioRows,
    getRiskAssessmentSubjectKey,
    getRiskAssessmentSubjectUrl,
    isAssessedScenario,
    isLevelOverAppetite,
    isMitigatable,
    isOverAppetiteScenario,
    isUncoveredOverAppetiteScenario,
    isUntreatedOverAppetiteScenario,
} from "../RiskAssessmentsOverview.utils";
import { boolParam, useSeedFilterFromUrl } from "@common/hooks/useSeedFilterFromUrl";
import { htmlToPlainText } from "@common/stringOperations";
import { useRowNoteDialog } from "@common/components/dotlegalNote/useRowNoteDialog";
import { useCustomListExport } from "../../../customLists/customListView/useCustomListExport";
import { RiskAssessmentLifecycleAction } from "../retireRiskAssessmentDialog/RetireRiskAssessmentDialog.hooks";
import { getCompletedMitigationStaleText, getDriftWarningText } from "../riskDriftWarning";
import { RiskExposureMode } from "../../riskExposure/RiskExposure.types";
import { useSelectableControlsForRisk } from "../../riskControls/RiskControls.hooks";
import { controlStatusFilterOptions, frameworkControlFilterOptions, yesNoFilterOptions } from "../../riskFilterUtils";
import { BulkMitigationSelection } from "../useBulkMitigationSelection";

const scenarioFilterId = "scenario";
const consequenceFilterId = "consequence";
const probabilityFilterId = "probability";
const riskFilterId = "risk";
const confidenceFilterId = "confidence";
const treatmentFilterId = "treatment";
const mitigationFilterId = "mitigation";
const assessedByFilterId = "assessedBy";
const participantsFilterId = "participants";
const hasParticipantsFilterId = "hasParticipants";
const assessmentDateFilterId = "assessmentDate";
const statusFilterId = "status";
const controlFilterId = "controls";
const controlStatusFilterId = "controlStatus";
const estimatedLossFilterId = "estimatedLoss";
const estimatedCostFilterId = "estimatedCost";
const exposureAfterMitigationFilterId = "exposureAfterMitigation";
const hasDocumentationFilterId = "hasDocumentation";
const hasNoteFilterId = "hasNote";
// The four the governance tiles deep-link on. Named for the property they state, not for the gap the tile states:
// the Assessed tile's "2 not assessed" seeds Assessed = Nej, the way the entity table's isAssessed filter does.
const isAssessedFilterId = "isAssessed";
const overAppetiteFilterId = "overAppetite";
const overAppetiteWithoutTreatmentFilterId = "overAppetiteWithoutTreatment";
const overAppetiteWithoutControlsFilterId = "overAppetiteWithoutControls";

// Must deep-equal the backend strategy's GetDefaultFilterData after a JSON round-trip, or the system default
// view would be flagged as changed before the user touches anything. Retired assessments are hidden by
// default; explicitly clearing the status filter (no status key) means "show all".
const defaultRiskAssessmentsFilter: RiskAssessmentsSavedFilter = {
    selectFilters: { [statusFilterId]: nonRetiredStatuses.map((status) => status.toString()) },
    dateFilters: {},
    numberFilters: {},
    filterOperators: {},
};

function isRiskAssessmentColumn(column: string): column is RiskAssessmentColumn {
    return allRiskAssessmentColumns.includes(column as RiskAssessmentColumn);
}

// The scenario table for one subject in one area, with its filters, saved views, export and row dialogs. Fetches
// the same overview endpoint the entity page's top bar reads; react-query keys on the URL, so the two observers
// share one request and one cache entry, and a refetch from here updates the page's tiles as well.
export function useScenarioAssessmentsTable(riskAreaId: string, subject: RiskAssessmentSubject, bulkMitigation: BulkMitigationSelection) {
    const { translateString, translateDate, translateFrameworkControlStatusEnum } = useTranslation();
    const { open: openPane } = usePanes();
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();
    const snackbar = useDotLegalSnackbar();
    const { exportRiskAssessmentInBackground } = useCustomListExport();

    // Saved views are scoped per area and subject. The same scope must reach useSavedViewQuery, usePageView and the
    // SavedViewsMenu props, or the menu and the table would read two different cache entries. The organisation has
    // no entity id, so its views sit in the area scope alone.
    const savedViewScopeKey = `${riskAreaId}:${getRiskAssessmentSubjectKey(subject)}`;
    const savedViewSecondaryScopeId = subject.kind === "entity" ? subject.entityId : undefined;

    const [searchString, setSearchString] = useState("");
    const [controlsDialogRow, setControlsDialogRow] = useState<RiskScenarioRow | null>(null);
    const [mitigatingScenario, setMitigatingScenario] = useState<RiskScenarioRow | undefined>(undefined);
    const [acceptingScenario, setAcceptingScenario] = useState<RiskScenarioRow | undefined>(undefined);
    const [avoidingScenario, setAvoidingScenario] = useState<RiskScenarioRow | undefined>(undefined);
    const [deletingScenario, setDeletingScenario] = useState<RiskScenarioRow | undefined>(undefined);
    const [assessingRow, setAssessingRow] = useState<RiskScenarioRow | null>(null);
    const [timelineScenario, setTimelineScenario] = useState<RiskScenarioRow | undefined>(undefined);
    const [lifecycleScenario, setLifecycleScenario] = useState<{ scenario: RiskScenarioRow; action: RiskAssessmentLifecycleAction } | undefined>(undefined);
    const savedViewQuery = useSavedViewQuery(FilterPage.RiskAssessmentsOverview, riskAreaId, savedViewSecondaryScopeId);
    const {
        currentViewId,
        getFilterData,
        getDisplayData,
        setFilterData,
        setDisplayData,
        hasFilterChanged,
        hasDisplayChanged,
        isSavedView,
        savedView,
        setView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView<RiskAssessmentsSavedFilter, Array<RiskAssessmentColumn>>(FilterPage.RiskAssessmentsOverview, savedViewQuery.data, savedViewScopeKey);

    const filterData = getFilterData<RiskAssessmentsSavedFilter>() ?? defaultRiskAssessmentsFilter;
    const { selectFilters, dateFilters, filterOperators } = filterData;
    // Views persisted before the exposure filters existed carry no numberFilters key, so indexing the raw value
    // would throw on the first render of an older saved view.
    const numberFilters = filterData.numberFilters ?? {};
    // The backend system-default view supplies the default columns once saved views resolve; until then the
    // table is in its loading state, so an empty fallback never renders.
    const visibleColumns = getDisplayData<Array<RiskAssessmentColumn>>() ?? [];

    // The organisation half of the governance tiles' deep links, mirroring the entity table's seeding so the same
    // tile reads the same way whichever section it lands on. Only this table is mounted when the operational tab is
    // on its organisation section, so a link naming that section seeds here.
    //
    // The default status filter is kept, unlike the entity table's, which drops its own: retired scenarios are out
    // of both halves of every tile figure (OrganisationScenarioTotal excludes them from the denominator and the
    // live posture excludes them from the numerator), so hiding them is what makes the count agree.
    useSeedFilterFromUrl(
        !savedViewQuery.isLoading,
        ["overAppetite", "overAppetiteWithoutControls", "overAppetiteWithoutTreatment", "notAssessed"],
        (params) => {
            const seeded: RiskAssessmentsSavedFilter = { ...defaultRiskAssessmentsFilter, selectFilters: { ...defaultRiskAssessmentsFilter.selectFilters } };

            const seedYesNo = (filterId: string, value: boolean | undefined) => {
                if (value !== undefined) seeded.selectFilters[filterId] = [value.toString()];
            };

            seedYesNo(overAppetiteFilterId, boolParam(params, "overAppetite"));
            seedYesNo(overAppetiteWithoutControlsFilterId, boolParam(params, "overAppetiteWithoutControls"));
            seedYesNo(overAppetiteWithoutTreatmentFilterId, boolParam(params, "overAppetiteWithoutTreatment"));
            // The param states the gap, the filter states the property: "not assessed" seeds Assessed = Nej.
            const notAssessed = boolParam(params, "notAssessed");
            seedYesNo(isAssessedFilterId, notAssessed === undefined ? undefined : !notAssessed);

            setFilterData(seeded);
        }
    );

    const queryClient = useDotLegalQueryClient();

    const detailUrl = getRiskAssessmentSubjectUrl(riskAreaId, subject);
    const { data, isLoading } = useDotLegalQuery<RiskAssessmentOverviewResult>(detailUrl);

    // Every save path refetches through the client by key rather than the observer's own refetch, so the page's
    // top bar, which observes the same key, is refreshed by the same call.
    const refetchOverview = () => {
        queryClient.refetchQueries(detailUrl);
    };

    // The documentation card lives above the router and has no onSave prop to call, so this list registers
    // its own refetch for the card to reach back through when a document is added or removed.
    useRefetchWhenPaneSaves(refetchOverview);

    const [isAddScenarioDialogOpen, setIsAddScenarioDialogOpen] = useState(false);

    const controlsQuery = useSelectableControlsForRisk();

    // What the subject is called where a name is shown: the entity's own name, or the word for the organisation.
    const subjectName = subject.kind === "entity" ? (data?.name ?? "") : translateString("organization");

    // The Treatment column shows the treatment decision (Mitigating / Risk accepted / Avoided); the
    // mitigation plan's sub-status lives in its own Mitigation column below.
    const getTreatmentLabel = (treatmentType: RiskTreatmentType) =>
        treatmentType === RiskTreatmentType.None ? "" : translateString(treatmentChipTranslationKey[treatmentType]);

    // Only the Mitigate treatment has a plan sub-status; Accept/Avoid/None leave the Mitigation column empty.
    const getMitigationStatusLabel = (treatmentType: RiskTreatmentType, mitigationPlanStatus: RiskScenarioRow["mitigationPlanStatus"]) =>
        treatmentType === RiskTreatmentType.Mitigate && mitigationPlanStatus != null
            ? translateString(mitigationStatusTranslationKey[mitigationPlanStatus])
            : "";

    const allRows: Array<RiskScenarioRow> = (data?.scenarios ?? []).map((scenario) => ({
        ...scenario,
        treatmentLabel: getTreatmentLabel(scenario.treatmentType),
        mitigationStatusLabel: getMitigationStatusLabel(scenario.treatmentType, scenario.mitigationPlanStatus),
        statusLabel: translateString(riskAssessmentStatusTranslationKey[scenario.status]),
        assessmentDateLabel: scenario.assessedAt ? translateDate(new Date(scenario.assessedAt)) : "",
        assessedByName: scenario.assessedByName ?? "",
        participantNamesLabel: scenario.participantNames.join(", "),
        driftWarningText: getDriftWarningText(scenario, translateString, translateDate),
        staleWarningText: getCompletedMitigationStaleText(scenario, translateString, translateDate),
        notePlainText: htmlToPlainText(scenario.note),
        confidenceLabel: scenario.confidence != null ? translateString(assessmentConfidenceTranslationKey[scenario.confidence]) : "",
    }));

    const appetite = data?.appetite ?? 0;
    const isOverAppetite = (level: number) => isLevelOverAppetite(level, appetite);

    const exposureMode = data?.exposureMode ?? RiskExposureMode.None;
    const exposureEnabled = exposureMode !== RiskExposureMode.None;
    const exposureCurrencyCode = data?.exposureCurrencyCode ?? null;
    const exposureScaleUpperBound = data?.exposureScaleUpperBound ?? null;

    // Retired assessments are hidden by the status filter carried in the default filter data; an explicitly
    // cleared status filter has no status key at all and falls through to "show all".
    const filteredRows = applyClientFilters(
        allRows,
        [
            { id: scenarioFilterId, candidate: (r) => r.name },
            { id: consequenceFilterId, candidate: (r) => r.consequenceLabel },
            { id: probabilityFilterId, candidate: (r) => r.probabilityLabel },
            { id: riskFilterId, candidate: (r) => r.riskLabel },
            { id: confidenceFilterId, candidate: (r) => r.confidence?.toString() ?? null },
            { id: treatmentFilterId, candidate: (r) => r.treatmentLabel },
            { id: mitigationFilterId, candidate: (r) => r.mitigationStatusLabel || null },
            { id: assessedByFilterId, candidate: (r) => r.assessedByName || null },
            // An array, so overlap matching: the row is kept when any of the selected users took part.
            { id: participantsFilterId, candidate: (r) => r.participantNames },
            // The names filter cannot express "nobody else took part", so solo ratings get their own yes/no filter.
            { id: hasParticipantsFilterId, candidate: (r) => (r.participantNames.length > 0).toString() },
            { id: statusFilterId, candidate: (r) => r.status.toString() },
            // linkedControlIds is an array, so matchesListFilter does overlap matching: the row is kept when it
            // links any of the selected controls (or, negated, none of them).
            { id: controlFilterId, candidate: (r) => r.linkedControlIds },
            { id: controlStatusFilterId, candidate: (r) => (r.linkedControlStatus != null ? r.linkedControlStatus.toString() : null) },
            // The Documentation column is a count, so it filters as yes/no.
            { id: hasDocumentationFilterId, candidate: (r) => (r.documentationCount > 0).toString() },
            { id: hasNoteFilterId, candidate: (r) => (r.note != null).toString() },
            // The governance tiles' four, each the frontend twin of the organisation fragment its figure is counted
            // with, so a tile's number equals the rows its deep link lands on.
            { id: isAssessedFilterId, candidate: (r) => isAssessedScenario(r).toString() },
            { id: overAppetiteFilterId, candidate: (r) => isOverAppetiteScenario(r, appetite).toString() },
            { id: overAppetiteWithoutTreatmentFilterId, candidate: (r) => isUntreatedOverAppetiteScenario(r, appetite).toString() },
            { id: overAppetiteWithoutControlsFilterId, candidate: (r) => isUncoveredOverAppetiteScenario(r, appetite).toString() },
        ],
        selectFilters,
        {},
        filterOperators,
        (r) =>
            matchesDateFilter(dateFilters[assessmentDateFilterId], r.assessedAt) &&
            matchesNumberFilter(numberFilters[estimatedLossFilterId], r.exposureValue) &&
            matchesNumberFilter(numberFilters[estimatedCostFilterId], r.planEstimatedCost) &&
            matchesNumberFilter(numberFilters[exposureAfterMitigationFilterId], r.planExpectedExposureValue)
    );

    const scenarioRows = filteredRows ?? [];

    const distinctOptions = (pairs: Array<IdNamePair>): Array<IdNamePair> => {
        const seen = new Map<string, IdNamePair>();
        pairs.forEach((pair) => {
            // Unscored scenarios have blank consequence/probability/risk labels — don't surface them as filter options.
            if (pair.name !== "" && !seen.has(pair.id)) seen.set(pair.id, pair);
        });
        return Array.from(seen.values()).sort((a, b) => (a.name > b.name ? 1 : -1));
    };

    const getFilterDataOptions = (): Array<FilterOption> => {
        const consequenceOptions = distinctOptions(allRows.map((r) => ({ id: r.consequenceLabel, name: r.consequenceLabel })));
        const probabilityOptions = distinctOptions(allRows.map((r) => ({ id: r.probabilityLabel, name: r.probabilityLabel })));
        const riskOptions = distinctOptions(allRows.map((r) => ({ id: r.riskLabel, name: r.riskLabel })));
        const treatmentOptions = distinctOptions(allRows.map((r) => ({ id: r.treatmentLabel, name: r.treatmentLabel })));
        const mitigationOptions = distinctOptions(allRows.map((r) => ({ id: r.mitigationStatusLabel, name: r.mitigationStatusLabel })));
        const assessedByOptions = distinctOptions(allRows.map((r) => ({ id: r.assessedByName, name: r.assessedByName })));
        const participantOptions = distinctOptions(allRows.flatMap((r) => r.participantNames.map((name) => ({ id: name, name }))));
        const scenarioOptions = distinctOptions(allRows.map((r) => ({ id: r.name, name: r.name })));

        const statusOptions = allRiskAssessmentStatuses.map((status) => ({
            id: status.toString(),
            name: translateString(riskAssessmentStatusTranslationKey[status]),
        }));

        const controlOptions = frameworkControlFilterOptions(controlsQuery.data);
        const controlStatusOptions = controlStatusFilterOptions(translateFrameworkControlStatusEnum);

        // Ordered scope, identity, risk picture, dates, treatment/controls, money — the same reading order the
        // other risk overviews use, so the bar looks the same wherever the user lands.
        const options: Array<FilterOption> = [
            {
                id: scenarioFilterId,
                label: translateString("scenario"),
                type: FilterOptionType.SelectField,
                value: selectFilters[scenarioFilterId] ?? [],
                fixedData: scenarioOptions,
            },
            {
                id: statusFilterId,
                label: translateString("assessmentStatus"),
                type: FilterOptionType.SelectField,
                value: selectFilters[statusFilterId] ?? [],
                fixedData: statusOptions,
            },
            {
                id: consequenceFilterId,
                label: translateString("consequence"),
                type: FilterOptionType.SelectField,
                value: selectFilters[consequenceFilterId] ?? [],
                fixedData: consequenceOptions,
            },
            {
                id: probabilityFilterId,
                label: translateString("probability"),
                type: FilterOptionType.SelectField,
                value: selectFilters[probabilityFilterId] ?? [],
                fixedData: probabilityOptions,
            },
            {
                id: riskFilterId,
                label: translateString("risk"),
                type: FilterOptionType.SelectField,
                value: selectFilters[riskFilterId] ?? [],
                fixedData: riskOptions,
            },
            {
                id: confidenceFilterId,
                label: translateString("assessmentConfidence"),
                type: FilterOptionType.SelectField,
                value: selectFilters[confidenceFilterId] ?? [],
                fixedData: getAssessmentConfidenceOptions(translateString),
            },
            {
                id: treatmentFilterId,
                label: translateString("treatment"),
                type: FilterOptionType.SelectField,
                value: selectFilters[treatmentFilterId] ?? [],
                fixedData: treatmentOptions,
            },
            {
                id: mitigationFilterId,
                label: translateString("mitigation"),
                type: FilterOptionType.SelectField,
                value: selectFilters[mitigationFilterId] ?? [],
                fixedData: mitigationOptions,
            },
            {
                id: assessedByFilterId,
                label: translateString("assessedBy"),
                type: FilterOptionType.SelectField,
                value: selectFilters[assessedByFilterId] ?? [],
                fixedData: assessedByOptions,
            },
            {
                id: participantsFilterId,
                label: translateString("participants"),
                type: FilterOptionType.SelectField,
                value: selectFilters[participantsFilterId] ?? [],
                fixedData: participantOptions,
            },
            {
                id: hasParticipantsFilterId,
                label: translateString("hasParticipants"),
                type: FilterOptionType.SelectField,
                value: selectFilters[hasParticipantsFilterId] ?? [],
                fixedData: yesNoFilterOptions(translateString),
            },
            {
                id: assessmentDateFilterId,
                label: translateString("assessmentDate"),
                type: FilterOptionType.DateField,
                value: dateFilters[assessmentDateFilterId] ?? "",
                hideFutureDates: true,
            },
            {
                id: controlFilterId,
                label: translateString("controls"),
                type: FilterOptionType.SelectField,
                value: selectFilters[controlFilterId] ?? [],
                fixedData: controlOptions,
            },
            {
                id: controlStatusFilterId,
                label: translateString("controlStatus"),
                type: FilterOptionType.SelectField,
                value: selectFilters[controlStatusFilterId] ?? [],
                fixedData: controlStatusOptions,
            },
            {
                id: hasDocumentationFilterId,
                label: translateString("hasDocumentation"),
                type: FilterOptionType.SelectField,
                value: selectFilters[hasDocumentationFilterId] ?? [],
                fixedData: yesNoFilterOptions(translateString),
            },
            {
                id: hasNoteFilterId,
                label: translateString("hasNote"),
                type: FilterOptionType.SelectField,
                value: selectFilters[hasNoteFilterId] ?? [],
                fixedData: yesNoFilterOptions(translateString),
            },
            // The governance tiles' landing filters. Same four labels the entity table uses, so the same concept
            // never reads two ways depending on which section the user landed in.
            {
                id: isAssessedFilterId,
                label: translateString("assessed"),
                type: FilterOptionType.SelectField,
                value: selectFilters[isAssessedFilterId] ?? [],
                fixedData: yesNoFilterOptions(translateString),
            },
            {
                id: overAppetiteFilterId,
                label: translateString("riskGovernanceOverAppetite"),
                type: FilterOptionType.SelectField,
                value: selectFilters[overAppetiteFilterId] ?? [],
                fixedData: yesNoFilterOptions(translateString),
            },
            {
                id: overAppetiteWithoutTreatmentFilterId,
                label: translateString("riskOverAppetiteWithoutTreatment"),
                type: FilterOptionType.SelectField,
                value: selectFilters[overAppetiteWithoutTreatmentFilterId] ?? [],
                fixedData: yesNoFilterOptions(translateString),
            },
            {
                id: overAppetiteWithoutControlsFilterId,
                label: translateString("riskOverAppetiteWithoutControls"),
                type: FilterOptionType.SelectField,
                value: selectFilters[overAppetiteWithoutControlsFilterId] ?? [],
                fixedData: yesNoFilterOptions(translateString),
            },
        ];

        // The exposure range filters are only meaningful once exposure is configured.
        if (exposureEnabled) {
            options.push(
                {
                    id: estimatedLossFilterId,
                    label: translateString("riskExposure"),
                    type: FilterOptionType.Number,
                    value: numberFilters[estimatedLossFilterId] ?? { from: undefined, to: undefined },
                },
                {
                    id: estimatedCostFilterId,
                    label: translateString("estimatedCost"),
                    type: FilterOptionType.Number,
                    value: numberFilters[estimatedCostFilterId] ?? { from: undefined, to: undefined },
                },
                {
                    id: exposureAfterMitigationFilterId,
                    label: translateString("exposureAfterMitigationLabel"),
                    type: FilterOptionType.Number,
                    value: numberFilters[exposureAfterMitigationFilterId] ?? { from: undefined, to: undefined },
                }
            );
        }

        return withOperators(options, filterOperators);
    };

    // Cleared filters are dropped (no empty arrays, no orphaned operators) so the persisted shape deep-equals
    // a saved view's JSON round-trip — otherwise change detection would flag phantom differences.
    const onFilterChange = (updatedOptions: Array<FilterOption>) => {
        const nextSelect: Record<string, Array<string>> = {};
        const nextDate: Record<string, DateFieldValue> = {};
        const nextNumber: Record<string, NumberIntervalValue> = {};
        const nextOperators: Record<string, FilterOperatorUi> = {};

        updatedOptions.forEach((option) => {
            if (option.type === FilterOptionType.SelectField) {
                const values = GetListData(option.value);
                if (values.length > 0) {
                    nextSelect[option.id] = values;
                    if (option.filterOperator !== undefined) {
                        nextOperators[option.id] = option.filterOperator;
                    }
                }
            }
            if (option.type === FilterOptionType.DateField) {
                const dateValue = GetDateData(option.value);
                if (hasDateRange(dateValue)) {
                    nextDate[option.id] = dateValue;
                }
            }
            if (option.type === FilterOptionType.Number) {
                const numberValue = GetNumberData(option.value);
                if (numberValue.from !== undefined || numberValue.to !== undefined) {
                    nextNumber[option.id] = numberValue;
                }
            }
        });

        setFilterData({ ...filterData, selectFilters: nextSelect, dateFilters: nextDate, numberFilters: nextNumber, filterOperators: nextOperators });
    };

    const onSortChange = (next: FilterSortState) => {
        setFilterData({ ...filterData, sortByColumn: next.orderBy, sortDirection: next.order });
    };

    const onColumnsSave = (columns: Array<string>) => {
        setDisplayData(columns.filter(isRiskAssessmentColumn));
    };

    const applyView = (viewId: string | undefined, filter: RiskAssessmentsSavedFilter, display: Array<RiskAssessmentColumn>) => {
        const updatedView: SessionStorageSavedView = {
            page: FilterPage.RiskAssessmentsOverview,
            viewId,
            filterData: filter,
            displayData: display,
        };
        setView(updatedView);
    };

    const onViewSaved = (viewId: string) => {
        applyView(viewId, filterData, visibleColumns);
    };

    const onViewDeleted = (deletedViewId: string) => {
        if (deletedViewId !== currentViewId) return;

        const fallbackView = savedViewQuery.data?.find((x: SavedView) => x.isCustomerDefault || x.isSystemDefault);
        applyView(fallbackView?.id === deletedViewId ? undefined : fallbackView?.id, filterData, visibleColumns);
    };

    const onChangeView = (view: SavedView) => {
        if (view.id !== currentViewId) {
            applyView(view.id, view.filterData, view.displayData);
        }
    };

    const tableFields: Array<TableFields> = [
        { id: RiskAssessmentColumn.Scenario, name: translateString("scenario"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Status, name: translateString("assessmentStatus"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Consequence, name: translateString("consequence"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Probability, name: translateString("probability"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Risk, name: translateString("risk"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Confidence, name: translateString("assessmentConfidence"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.AssessmentDate, name: translateString("assessmentDate"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.AssessedBy, name: translateString("assessedBy"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Participants, name: translateString("participants"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Treatment, name: translateString("treatment"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Mitigation, name: translateString("mitigation"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.ControlCount, name: translateString("controlCount"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.ControlStatus, name: translateString("controlStatus"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Documentation, name: translateString("documentation"), grouping: RiskFieldGrouping.Risk },
        { id: RiskAssessmentColumn.Note, name: translateString("note"), grouping: RiskFieldGrouping.Risk },
        ...(exposureEnabled
            ? [
                  { id: RiskAssessmentColumn.EstimatedLoss, name: translateString("riskExposure"), grouping: RiskFieldGrouping.Risk },
                  { id: RiskAssessmentColumn.EstimatedCost, name: translateString("estimatedCost"), grouping: RiskFieldGrouping.Risk },
                  { id: RiskAssessmentColumn.ExposureAfterMitigation, name: translateString("exposureAfterMitigationLabel"), grouping: RiskFieldGrouping.Risk },
              ]
            : []),
    ];

    // Render only persisted columns that are still selectable. tableFields omits the exposure columns when
    // exposure is not configured, so this drops them from the system-default view instead of showing a blank.
    // While the data (and thus the exposure mode) is still loading, tableFields hasn't yet gained the exposure
    // columns, so keep the selected columns as-is until we know the configuration to avoid transiently dropping
    // a selected exposure column from the header.
    const renderedColumns = isLoading ? visibleColumns : visibleColumns.filter((column) => tableFields.some((field) => field.id === column));

    // The table's current sort, falling back to the risk column highest-risk-first until the user sorts a column.
    // Shared by the table config (defaultOrderBy/defaultOrder) and the export so both order rows identically.
    const sortOrderBy = filterData.sortByColumn ?? "riskLabel";
    const sortOrder = filterData.sortDirection ?? "asc";

    // Filtering/sort/search are client-side here, so the export mirrors the table exactly: it sends the on-screen
    // rows (filter bar + search box + current sort) in order, carrying each row's already-resolved control-status
    // rollup, and the backend re-fetches + formats the rest.
    const onDownloadExcelReportClick = async () => {
        const headerByColumn = new Map(tableFields.map((field) => [field.id, field.name]));
        const headers: Array<RiskAssessmentExportHeader> = renderedColumns.map((column) => ({
            header: headerByColumn.get(column) ?? "",
            column,
        }));

        const displayedRows = getDisplayedScenarioRows(scenarioRows, searchString, sortOrderBy, sortOrder);
        const rows = displayedRows.map((row) => ({ assessmentId: row.id, controlStatus: row.linkedControlStatus }));

        // Mirrors the page breadcrumb, but hyphenated rather than using ":" to read well as the Excel sheet tab the
        // report name becomes. The generator, not this call site, enforces Excel's sheet naming rules. The filename
        // is uncapped, so only it also carries the active view, which distinguishes two exports of the same entity.
        const reportName = `${translateString("riskAssessment")} - ${subjectName}`;
        const fileNameBase = `${reportName} - ${savedView!.name}`;

        await exportRiskAssessmentInBackground(
            reportName,
            headers,
            {
                riskAreaId,
                entityType: subject.kind === "entity" ? subject.entityType : null,
                entityId: subject.kind === "entity" ? subject.entityId : null,
                rows,
            },
            fileNameBase
        );
        snackbar.show(translateString("riskOperationalDataExportInProgress"), "success", true);
    };

    const onAddScenario = () => setIsAddScenarioDialogOpen(true);
    const closeAddScenarioDialog = () => setIsAddScenarioDialogOpen(false);
    const onScenariosAdded = () => refetchOverview();

    const onAssess = (row: RiskScenarioRow) => setAssessingRow(row);
    const closeAssessDialog = () => setAssessingRow(null);

    const { getNoteMenuItem, noteDialogProps } = useRowNoteDialog<RiskScenarioRow>({
        getPostUrl: (row) => `/risk-assessments/${row.id}/note`,
        disableMentions: true,
        onSaved: refetchOverview,
    });

    // "Select all" and the header checkbox act on the mitigatable rows in the current filtered view, so a
    // freshly-toggled select-all matches exactly what the bulk endpoint will create plans for.
    const mitigatableRows = scenarioRows.filter(isMitigatable);

    const onBulkMitigationSaved = () => {
        bulkMitigation.exitSelectionMode();
        bulkMitigation.closeDialog();
        refetchOverview();
    };

    const onRetire = (row: RiskScenarioRow) => setLifecycleScenario({ scenario: row, action: "retire" });
    const onReinstate = (row: RiskScenarioRow) => setLifecycleScenario({ scenario: row, action: "reinstate" });

    const onMitigate = (row: RiskScenarioRow) => setMitigatingScenario(row);
    const onAcceptRisk = (row: RiskScenarioRow) => setAcceptingScenario(row);
    const onAvoid = (row: RiskScenarioRow) => setAvoidingScenario(row);

    // The cell click opens the editor for whatever treatment is currently active. A worsened acceptance routes
    // to the accept dialog (re-accept), never reconfirm. None has no current treatment — the user picks the
    // first one from the overflow menu instead.
    const onOpenCurrentTreatment = (row: RiskScenarioRow) => {
        switch (row.treatmentType) {
            case RiskTreatmentType.Mitigate:
                onMitigate(row);
                break;
            case RiskTreatmentType.Accept:
                onAcceptRisk(row);
                break;
            case RiskTreatmentType.Avoid:
                onAvoid(row);
                break;
            default:
                break;
        }
    };

    const onTreatmentSaved = (clear: () => void) => () => {
        refetchOverview();
        clear();
    };

    // Jumps to the area's Mitigation tab pre-filtered to this scenario on this entity. Deliberately does NOT set
    // the status filter, so the row stays visible as the user advances the plan through its statuses there. The
    // organisation has no entity to filter on, so its jump narrows to the scenario alone.
    const onGoToMitigation = (row: RiskScenarioRow) => {
        navigate(
            urlProvider.getRiskMitigationOverviewUrl(riskAreaId, {
                scenarioIds: [row.riskScenarioId],
                ...(subject.kind === "entity" ? { entityIds: [subject.entityId] } : {}),
            })
        );
    };

    return {
        pinnedFilterIds,
        setPinnedFilterIds,
        isLoading: isLoading || savedViewQuery.isLoading,
        hasScenarios: isLoading || (data?.scenarios?.length ?? 0) > 0,
        subjectName,
        savedViewSecondaryScopeId,
        scenarioRows,
        mitigatableRows,
        isOverAppetite,
        getFilterDataOptions,
        onFilterChange,
        sortOrderBy,
        sortOrder,
        onSortChange,
        visibleColumns,
        renderedColumns,
        exposureSummary: data?.exposure ?? null,
        exposureMode,
        exposureCurrencyCode,
        exposureScaleUpperBound,
        tableFields,
        onColumnsSave,
        onDownloadExcelReportClick,
        filterData,
        currentViewId,
        savedView,
        isSavedView,
        hasFilterChanged,
        hasDisplayChanged,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        searchString,
        setSearchString,
        onAddScenario,
        isAddScenarioDialogOpen,
        closeAddScenarioDialog,
        onScenariosAdded,
        onAssess,
        assessingRow,
        closeAssessDialog,
        getNoteMenuItem,
        noteDialogProps,
        refetchOverview,
        onMitigate,
        onAcceptRisk,
        onAvoid,
        onGoToMitigation,
        onOpenCurrentTreatment,
        onDelete: setDeletingScenario,
        mitigatingScenario,
        acceptingScenario,
        avoidingScenario,
        closeMitigateDialog: () => setMitigatingScenario(undefined),
        closeAcceptDialog: () => setAcceptingScenario(undefined),
        closeAvoidDialog: () => setAvoidingScenario(undefined),
        onMitigationSaved: onTreatmentSaved(() => setMitigatingScenario(undefined)),
        onAcceptanceSaved: onTreatmentSaved(() => setAcceptingScenario(undefined)),
        onAvoidanceSaved: onTreatmentSaved(() => setAvoidingScenario(undefined)),
        deletingScenario,
        closeDeleteDialog: () => setDeletingScenario(undefined),
        onScenarioDeleted: () => {
            refetchOverview();
            setDeletingScenario(undefined);
        },
        onViewTimeline: setTimelineScenario,
        timelineScenario,
        closeTimeline: () => setTimelineScenario(undefined),
        onDocumentation: (scenario: RiskScenarioRow) => openPane(RiskDocumentationPane, { riskAssessmentId: scenario.id }),
        onRetire,
        onReinstate,
        lifecycleScenario,
        closeLifecycleDialog: () => setLifecycleScenario(undefined),
        onLifecycleSaved: () => {
            refetchOverview();
            setLifecycleScenario(undefined);
        },
        controlsDialogRow,
        openControlsDialog: setControlsDialogRow,
        closeControlsDialog: () => setControlsDialogRow(null),
        onBulkMitigationSaved,
    };
}
